import React, { ReactElement, useCallback, useContext, useMemo } from 'react';
import { Button, Dropdown, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { ControlOutlined } from '@ant-design/icons';
import rolesConstants, { RolesTypes } from '@utils/rolesConstants';
import SessionContext from '@providers/Session/SessionContext';
import {
  ApplicationsConstants,
  motProducts,
} from '@utils/applicationsConstants';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import { actionsItems, CustomerType, MenuItemsProps } from './ActionsItems';

interface ActionsMenuProps {
  onActionClick: (role: RolesTypes) => void;
  isStock?: boolean;
  showContracted?: string;
  button?: ReactElement;
  className?: string;
  isLoraWan?: boolean;
  isUpload?: boolean;
}

const ActionsMenu: React.FC<ActionsMenuProps> = ({
  onActionClick,
  isStock,
  showContracted,
  button,
  className,
  isLoraWan,
  isUpload,
}) => {
  const { t } = useTranslation('device-actions');
  const {
    isRoot,
    effectiveRoles,
    selectedApp,
    isNotChildOfRoot,
    hasPermission,
  } = useContext(SessionContext);
  const { keycloak } = useContext(KeycloakContext);
  const rolesToAccess = effectiveRoles?.portal;
  const customerTypeLogged = keycloak.idTokenParsed?.customerType;
  const isEndCustomer = customerTypeLogged === 'CUSTOMER';

  const menuItems = useMemo(() => {
    const hasNewLora = hasPermission(rolesConstants.LORAWAN_NEW);
    return actionsItems({
      isStock,
      selectedApp,
      rolesToAccess,
      customerType: customerTypeLogged as CustomerType,
      showContracted: showContracted === 'true' || isEndCustomer,
      isLoraWan,
      hasNewLora,
    });
  }, [
    hasPermission,
    isStock,
    selectedApp,
    rolesToAccess,
    customerTypeLogged,
    showContracted,
    isEndCustomer,
    isLoraWan,
  ]);

  const renderLabel = useCallback(
    (item: MenuItemsProps, title: string, disabled?: boolean) => {
      const renderText = (
        <p
          style={{
            padding: '2px 12px',
            marginBottom: 2,
          }}
        >
          {title}
        </p>
      );
      return disabled ? (
        <Tooltip key={item.role} title={t('root-cannot-see')}>
          {renderText}
        </Tooltip>
      ) : (
        renderText
      );
    },
    [t],
  );

  const items = useMemo(
    () =>
      menuItems
        ?.filter(item => {
          const isOnRightProduct =
            !!selectedApp && item.products?.includes(selectedApp);

          const isCustomer = customerTypeLogged === 'CUSTOMER';
          const customerCantSee =
            isCustomer &&
            item.productThatCustomerCannotSee?.includes(
              selectedApp as ApplicationsConstants,
            );
          const customerByTypeThatCanSee =
            !item.whoCantSee?.includes(customerTypeLogged as CustomerType) &&
            !customerCantSee;

          const havePermission = rolesToAccess?.includes(item.role);

          const isCorrectNetwork = isLoraWan
            ? item?.isLoraWan
            : !item?.isLoraWan;

          const canShowByHierarchy = !(
            isNotChildOfRoot && item?.isOnlyAlgarCustomer
          );

          return (
            isOnRightProduct &&
            customerByTypeThatCanSee &&
            havePermission &&
            isCorrectNetwork &&
            canShowByHierarchy
          );
        })
        ?.map(item => {
          const rootCantSee =
            isRoot && item.whoCantSee && item.whoCantSee.includes('ROOT');

          let labelApp = 'mot';
          if (isLoraWan) {
            labelApp = ApplicationsConstants.LORAWAN;
          } else if (selectedApp && motProducts.includes(selectedApp)) {
            labelApp = 'mot';
          }

          const treatedAlternativeTitle = () => {
            const canSee = item.whoCanSeeAlternativeTitle?.includes(
              customerTypeLogged as CustomerType,
            );

            return canSee ? item.alternativeTitle : item.role;
          };

          const title = t(
            `labels.${labelApp ?? selectedApp}.${
              item?.alternativeTitle ? treatedAlternativeTitle() : item.role
            }`,
          );

          return {
            disabled: rootCantSee || (item?.isOnlyUpload && !isUpload),
            key: item.role,
            title,
            label: renderLabel(item, title, rootCantSee),
          };
        }),
    [
      menuItems,
      selectedApp,
      customerTypeLogged,
      rolesToAccess,
      isLoraWan,
      isNotChildOfRoot,
      isRoot,
      t,
      isUpload,
      renderLabel,
    ],
  );

  items?.sort((a, b) => a.title.localeCompare(b.title));

  if (items?.length === 0) {
    return <></>;
  }

  return (
    <Dropdown
      overlayClassName="overlay-menu"
      placement="bottomRight"
      menu={{ items, onClick: ({ key }) => onActionClick(key as RolesTypes) }}
      className={className}
      trigger={['click']}
    >
      {button ?? (
        <Tooltip title={t('actions')}>
          <Button icon={<ControlOutlined />} />
        </Tooltip>
      )}
    </Dropdown>
  );
};

export default ActionsMenu;
